"use client"

// =====================================================
// BOOTH — TikTok Pixel loader + SPA pageview tracking
// =====================================================
// Mirrors components/analytics/meta-pixel.tsx one-for-one:
//
//   1. TikTokPixel         — injects the standard TikTok base snippet
//                            once, which calls ttq.load(<id>) and fires
//                            the first ttq.page().
//   2. TikTokPixelPageView — fires page() on subsequent App Router
//                            navigations. Next's client router doesn't
//                            reload the page, so without this the pixel
//                            only ever sees the entry URL.
//
// Both render nothing and both no-op when NEXT_PUBLIC_TIKTOK_PIXEL_ID is
// unset, so local dev and preview builds stay clean of ad tracking.
//
// The OAuth signup conversion is NOT here — it's shared with Meta in
// components/analytics/signup-conversion.tsx, because both pixels read
// the same one-shot ?booth_signup flag and whichever ran first would
// otherwise strip it before the other saw it.
// =====================================================

import Script from "next/script"
import { Suspense, useEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { TIKTOK_PIXEL_ID, trackTikTokPageView } from "@/lib/tiktok-pixel"

export function TikTokPixel() {
  if (!TIKTOK_PIXEL_ID) return null

  return (
    <Script
      id="tiktok-pixel"
      strategy="afterInteractive"
      // TikTok's official base code, verbatim apart from the interpolated
      // pixel id. Next dedupes by the `id` above, so it injects once.
      dangerouslySetInnerHTML={{
        __html: `
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{},n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(n,s)};

  ttq.load('${TIKTOK_PIXEL_ID}');
  ttq.page();
}(window, document, 'ttq');
        `,
      }}
    />
  )
}

function TikTokPixelPageViewInner() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // The base snippet already fired page() for the entry URL. Skipping the
  // first effect run keeps the landing hit from being counted twice, which
  // would otherwise inflate every ad's reported reach.
  const skippedInitial = useRef(false)

  useEffect(() => {
    if (!TIKTOK_PIXEL_ID || !pathname) return
    if (!skippedInitial.current) {
      skippedInitial.current = true
      return
    }
    trackTikTokPageView()
  }, [pathname, searchParams])

  return null
}

// useSearchParams() opts the subtree into client rendering, so it needs a
// Suspense boundary to avoid de-opting the whole route (same as MetaPixelPageView).
export function TikTokPixelPageView() {
  return (
    <Suspense fallback={null}>
      <TikTokPixelPageViewInner />
    </Suspense>
  )
}
